import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity} from 'react-native';
import {TextInput, Button, Snackbar, Text} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const OrderSettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();

  const [prefix, setPrefix] = useState('');
  const [serialStart, setSerialStart] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [measurementUnit, setMeasurementUnit] = useState('INCHES');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [orgUnits.length, fetchOrgUnits]);

  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (effectiveOrgUnitId) {
      fetchByType('ORDER', effectiveOrgUnitId);
    }
  }, [fetchByType, effectiveOrgUnitId]);

  useEffect(() => {
    if (effectiveOrgUnitId) {
      setPrefix(getProperty('ORDER', 'ORDER_PREFIX', effectiveOrgUnitId) || 'ORD');
      setSerialStart(getProperty('ORDER', 'ORDER_SERIAL_START', effectiveOrgUnitId) || '1');
      setDeliveryDays(getProperty('ORDER', 'DELIVERY_DAYS', effectiveOrgUnitId) || '4');
      setMeasurementUnit(getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES');
    }
  }, [getProperty, effectiveOrgUnitId, useConfigStore.getState().properties]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProperties('ORDER', [
        {code: 'ORDER_PREFIX', name: 'Order Prefix', value: prefix},
        {code: 'ORDER_SERIAL_START', name: 'Order Serial Start', value: serialStart},
        {code: 'DELIVERY_DAYS', name: 'Delivery Days', value: deliveryDays},
        {code: 'MEASUREMENT_UNIT', name: 'Measurement Unit', value: measurementUnit},
      ], effectiveOrgUnitId);
      setSnackbar(t('config.saved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [prefix, serialStart, deliveryDays, measurementUnit, updateProperties, effectiveOrgUnitId, t]);

  const storeRequired = orgUnits.length > 1 && !selectedOrgUnitId;

  return (
    <View style={styles.container}>
      <GradientHeader title={t('config.orderSettings')} onBack={() => navigation.goBack()}>
        <OrgUnitSelector variant="light" />
      </GradientHeader>

      {storeRequired ? (
        <View style={styles.emptyContainer}>
          <Icon name="store-cog-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyText}>
            {t('config.selectStorePrompt', {defaultValue: 'Please select a store to configure order settings.'})}
          </Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* Order Number */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Icon name="pound" size={18} color={colors.primary} />
              <Text style={styles.sectionTitle}>Order Number</Text>
            </View>
            <Text style={styles.sectionHint}>{t('config.orderPrefixDesc')}</Text>

            <View style={styles.row}>
              <TextInput
                label={t('config.orderPrefix')}
                value={prefix}
                onChangeText={setPrefix}
                mode="outlined"
                style={[styles.input, styles.halfInput]}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
              <TextInput
                label={t('config.serialNumber')}
                value={serialStart}
                onChangeText={setSerialStart}
                mode="outlined"
                keyboardType="numeric"
                style={[styles.input, styles.halfInput]}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            </View>
            <Text style={styles.previewText}>
              Preview: {prefix || 'ORD'}-{String(serialStart || '1').padStart(4, '0')}
            </Text>
          </View>

          {/* Delivery Days */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Icon name="truck-delivery-outline" size={18} color={colors.primary} />
              <Text style={styles.sectionTitle}>{t('config.deliveryDays')}</Text>
            </View>
            <Text style={styles.sectionHint}>{t('config.deliveryDaysDesc')}</Text>

            <TextInput
              label={t('config.deliveryDays')}
              value={deliveryDays}
              onChangeText={setDeliveryDays}
              mode="outlined"
              keyboardType="numeric"
              style={styles.input}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />
          </View>

          {/* Measurement Unit */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Icon name="ruler" size={18} color={colors.primary} />
              <Text style={styles.sectionTitle}>Measurement Unit</Text>
            </View>
            <Text style={styles.sectionHint}>
              Unit used for capturing all measurements (body & dress)
            </Text>

            <View style={styles.unitToggleRow}>
              {['INCHES', 'CMS'].map(unit => {
                const selected = measurementUnit === unit;
                return (
                  <TouchableOpacity
                    key={unit}
                    style={[styles.unitOption, selected && styles.unitOptionSelected]}
                    onPress={() => setMeasurementUnit(unit)}
                    activeOpacity={0.7}>
                    <Icon
                      name={unit === 'INCHES' ? 'ruler' : 'ruler-square'}
                      size={18}
                      color={selected ? colors.primary : colors.textSecondary}
                    />
                    <Text style={[styles.unitOptionText, selected && styles.unitOptionTextSelected]}>
                      {unit === 'INCHES' ? 'Inches' : 'Centimeters'}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          <Button
            mode="contained"
            onPress={handleSave}
            loading={saving}
            disabled={saving || isLoading}
            style={styles.saveBtn}
            buttonColor={colors.primary}>
            {t('common.save')}
          </Button>
        </ScrollView>
      )}

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollContent: {paddingBottom: spacing.xl},

  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  sectionHint: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    marginLeft: 24,
  },
  row: {flexDirection: 'row', gap: spacing.sm},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  halfInput: {flex: 1},
  previewText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
    fontStyle: 'italic',
    marginTop: -4,
  },
  saveBtn: {marginHorizontal: spacing.md, marginTop: spacing.sm, borderRadius: 8},

  // Unit toggle
  unitToggleRow: {flexDirection: 'row', gap: spacing.sm},
  unitOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: spacing.sm + 2,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  unitOptionSelected: {borderColor: colors.primary, backgroundColor: colors.primary + '10'},
  unitOptionText: {fontSize: 14, fontWeight: '500', color: colors.textSecondary},
  unitOptionTextSelected: {color: colors.primary, fontWeight: '700'},

  // Empty
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, gap: spacing.md},
  emptyText: {color: colors.textSecondary, textAlign: 'center', fontSize: 15},
});

export default OrderSettingsScreen;
